/**
 * Índice SEO de destinos — /destinos
 * Cada card enlaza a /tokio, /kioto… (no a la preview).
 */

import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Box, Typography, Grid, Button } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PublicIcon from "@mui/icons-material/Public";
import { useFirebase } from "../context/ApiContext";
import { DashboardLayout } from "../components/Dashboard";
import Auth from "../components/Auth/Auth";
import { BasicModal } from "../components/BasicModal/BasicModal";
import SeoHead from "../components/Seo/SeoHead";
import { GUIDE_LANDINGS, guideLandingPath } from "../data/guideLandings";
import { destinosJsonLd } from "../utils/seoSchema";
import { getSiteOrigin } from "../config/site";

import "../components/Dashboard/Dashboard.css";

import destinosKioto from "../assets/destinos/kioto.png";
import destinosTokio from "../assets/destinos/tokio.png";
import destinosOsaka from "../assets/destinos/osaka.png";
import destinosNY from "../assets/destinos/nueva-york.png";
import destinosSanFrancisco from "../assets/destinos/san-francisco.png";
import destinosLondres from "../assets/destinos/londres.png";
import destinosParis from "../assets/destinos/paris.png";
import destinosSeul from "../assets/destinos/seul.png";
import destinosBangkok from "../assets/destinos/bangkok.png";
import destinosKualaLumpur from "../assets/destinos/kuala-lumpur.png";
import destinosBelgica from "../assets/destinos/belgica.png";
import destinosPekin from "../assets/destinos/pekin.png";
import destinosBali from "../assets/destinos/bali.png";
import destinosEdimburgo from "../assets/destinos/edimburgo.png";

const coverMap = {
	kioto: destinosKioto,
	tokio: destinosTokio,
	osaka: destinosOsaka,
	ny: destinosNY,
	sanFrancisco: destinosSanFrancisco,
	londres: destinosLondres,
	paris: destinosParis,
	seul: destinosSeul,
	bangkok: destinosBangkok,
	kualaLumpur: destinosKualaLumpur,
	belgica: destinosBelgica,
	pekin: destinosPekin,
	bali: destinosBali,
	edimburgo: destinosEdimburgo,
};

const REGION_BY_COUNTRY = {
	Japón: "asia",
	"Corea del Sur": "asia",
	Tailandia: "asia",
	Malasia: "asia",
	China: "asia",
	Indonesia: "asia",
	"Estados Unidos": "america",
	"Reino Unido": "europa",
	Escocia: "europa",
	Francia: "europa",
	Bélgica: "europa",
};

const REGIONS = [
	{ id: "all", label: "Todos" },
	{ id: "asia", label: "Asia" },
	{ id: "europa", label: "Europa" },
	{ id: "america", label: "América" },
];

const ACCENT = "#6366F1";

const DestinosPage = () => {
	const navigate = useNavigate();
	const { enqueueSnackbar } = useSnackbar();
	const { user, handleLogout: contextLogout } = useFirebase();
	const [showLogin, setShowLogin] = useState(false);
	const [authMode, setAuthMode] = useState("login");
	const [region, setRegion] = useState("all");

	const destinos = useMemo(() => {
		const list = Object.values(GUIDE_LANDINGS).map((landing) => ({
			...landing,
			region: REGION_BY_COUNTRY[landing.country] || "all",
		}));
		if (region === "all") return list;
		return list.filter((d) => d.region === region);
	}, [region]);

	const formattedUser = user
		? {
				id: user.uid || user.id,
				displayName:
					user.displayName ||
					user.name ||
					user.email?.split("@")[0] ||
					"Usuario",
				email: user.email,
			}
		: null;

	const handleSectionChange = (sectionId) => {
		if (sectionId === "home" || sectionId === "recent") {
			navigate("/");
			return;
		}
		if (sectionId === "explore") {
			navigate("/explorar");
			return;
		}
		if (sectionId === "destinos") return;
		if (sectionId === "my-guides") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				enqueueSnackbar("Inicia sesión para ver tus guías", {
					variant: "info",
				});
				return;
			}
			navigate("/mis-guias");
			return;
		}
		if (sectionId === "favorites") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				return;
			}
			navigate("/favoritos");
			return;
		}
		if (sectionId === "passport") {
			navigate("/passport");
			return;
		}
		if (sectionId === "travel-card") {
			navigate("/tarjeta-viajera");
			return;
		}
		if (sectionId === "modules") {
			if (!user) {
				setAuthMode("login");
				setShowLogin(true);
				return;
			}
			navigate("/modulos");
			return;
		}
		if (sectionId === "discounts") navigate("/descuentos");
		if (sectionId === "create") {
			navigate("/crear-guia");
		}
	};

	const handleLogout = async () => {
		await contextLogout();
		enqueueSnackbar("Sesión cerrada", { variant: "success" });
	};

	return (
		<>
			<SeoHead
				title="Guías de viaje por destino"
				description="Guías de viaje digitales por ciudad: Tokio, Kioto, Nueva York, París, Seúl y más. Itinerario, tips y preview gratis."
				path="/destinos"
				jsonLd={destinosJsonLd(getSiteOrigin(), GUIDE_LANDINGS)}
			/>
			<DashboardLayout
				user={formattedUser}
				activeSection="destinos"
				onSectionChange={handleSectionChange}
				onLogin={() => {
					setAuthMode("login");
					setShowLogin(true);
				}}
				onSignUp={() => {
					setAuthMode("signup");
					setShowLogin(true);
				}}
				onLogout={handleLogout}
				onCreateNew={() => navigate("/crear-guia")}
				onSearch={() => navigate("/explorar")}
			>
				<Box sx={{ mt: { xs: 2, md: 0 } }}>
					<Box
						sx={{
							display: "flex",
							alignItems: { sm: "flex-end" },
							justifyContent: "space-between",
							gap: 2,
							mb: 2.5,
						}}
					>
						<Box>
							<Typography
								component="h1"
								sx={{
									fontWeight: 700,
									fontSize: { xs: "1.25rem", md: "1.5rem" },
									color: "#1a1a2e",
									letterSpacing: "-0.02em",
									lineHeight: 1.2,
									mb: 0.5,
								}}
							>
								Destinos
							</Typography>
							<Typography
								sx={{
									color: "#64748B",
									fontSize: { xs: "0.875rem", sm: "0.938rem" },
									lineHeight: 1.5,
								}}
							>
								Itinerario, tips y información de cada ciudad / país
							</Typography>
						</Box>
						<Typography
							sx={{
								display: { xs: "none", sm: "block" },
								fontSize: "0.8125rem",
								fontWeight: 600,
								color: "#94A3B8",
								whiteSpace: "nowrap",
								pb: 0.25,
							}}
						>
							{destinos.length}{" "}
							{destinos.length === 1 ? "ciudad" : "ciudades"}
						</Typography>
					</Box>

					<Box
						sx={{
							display: "flex",
							gap: { xs: 0.75, sm: 1 },
							mb: 3,
							flexWrap: "nowrap",
							overflowX: "auto",
							pt: 0.5,
							mt: -0.5,
							"&::-webkit-scrollbar": { display: "none" },
							scrollbarWidth: "none",
						}}
					>
						{REGIONS.map((r) => {
							const active = region === r.id;
							return (
								<Button
									key={r.id}
									variant="text"
									disableElevation
									onClick={() => setRegion(r.id)}
									sx={{
										textTransform: "none",
										fontWeight: 600,
										fontSize: "0.838rem",
										whiteSpace: "nowrap",
										flexShrink: 0,
										px: 2.25,
										py: 0.875,
										borderRadius: "50px",
										minWidth: "auto",
										color: active
											? "#312E81 !important"
											: "#475569 !important",
										backgroundColor: active
											? "rgba(99, 102, 241, 0.14) !important"
											: "#fff !important",
										border: active
											? "1.5px solid #6366F1 !important"
											: "1.5px solid rgba(15, 23, 42, 0.1) !important",
										boxShadow: "none",
										"&:hover": {
											backgroundColor: active
												? "rgba(99, 102, 241, 0.2) !important"
												: "rgba(99, 102, 241, 0.06) !important",
											color: active
												? "#312E81 !important"
												: "#4F46E5 !important",
											border: active
												? "1.5px solid #4F46E5 !important"
												: "1.5px solid rgba(99, 102, 241, 0.35) !important",
										},
									}}
								>
									{r.label}
								</Button>
							);
						})}
					</Box>

					<Grid container spacing={{ xs: 1.5, sm: 2.5 }}>
						{destinos.map((landing, index) => (
							<Grid item xs={6} sm={6} md={4} lg={3} key={landing.slug}>
								<Box
									component={Link}
									to={guideLandingPath(landing.guideId)}
									state={{ from: "/destinos" }}
									sx={{
										display: "flex",
										flexDirection: "column",
										height: "100%",
										textDecoration: "none",
										borderRadius: { xs: "14px", sm: "20px" },
										overflow: "hidden",
										border: "1px solid rgba(0, 0, 0, 0.06)",
										backgroundColor: "#fff",
										animation: "fadeInUp 0.4s ease-out forwards",
										animationDelay: `${index * 50}ms`,
										opacity: 0,
										transition:
											"transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease, border-color 0.3s ease",
										"&:hover": {
											transform: { sm: "translateY(-6px)" },
											boxShadow: {
												sm: "0 16px 36px rgba(15, 23, 42, 0.1)",
											},
											borderColor: { sm: "transparent" },
											"& .destino-cover": {
												transform: { sm: "scale(1.05)" },
											},
											"& .destino-arrow": {
												transform: "translateX(3px)",
												color: ACCENT,
											},
										},
									}}
								>
									<Box
										sx={{
											position: "relative",
											overflow: "hidden",
											aspectRatio: { xs: "4 / 5", sm: "1 / 1" },
											backgroundColor: "#f1f5f9",
										}}
									>
										<Box
											component="img"
											className="destino-cover"
											src={coverMap[landing.guideId]}
											alt={`Guía de ${landing.name}`}
											sx={{
												width: "100%",
												height: "100%",
												objectFit: "cover",
												display: "block",
												transition:
													"transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
											}}
										/>
										{landing.days ? (
											<Box
												sx={{
													position: "absolute",
													bottom: { xs: 8, sm: 12 },
													left: { xs: 8, sm: 12 },
													display: "inline-flex",
													alignItems: "center",
													gap: 0.5,
													px: { xs: 0.875, sm: 1.25 },
													py: { xs: 0.25, sm: 0.5 },
													borderRadius: { xs: "6px", sm: "8px" },
													backgroundColor: "rgba(0, 0, 0, 0.6)",
													backdropFilter: "blur(8px)",
												}}
											>
												<AccessTimeIcon
													sx={{
														fontSize: { xs: 12, sm: 14 },
														color: "#fff",
													}}
												/>
												<Typography
													sx={{
														fontSize: { xs: "0.688rem", sm: "0.75rem" },
														fontWeight: 600,
														color: "#fff",
														lineHeight: 1,
													}}
												>
													{landing.days} días
												</Typography>
											</Box>
										) : null}
									</Box>

									<Box
										sx={{
											p: { xs: 1.5, sm: 2 },
											display: "flex",
											alignItems: "flex-start",
											justifyContent: "space-between",
											gap: 1,
										}}
									>
										<Box sx={{ minWidth: 0 }}>
											<Typography
												sx={{
													fontWeight: 700,
													fontSize: { xs: "0.875rem", sm: "1.05rem" },
													color: "#1a1a2e",
													letterSpacing: "-0.02em",
													lineHeight: 1.25,
													mb: 0.35,
												}}
											>
												{landing.name}
											</Typography>
											<Typography
												sx={{
													color: "#64748B",
													fontSize: { xs: "0.75rem", sm: "0.8125rem" },
													fontWeight: 500,
												}}
											>
												{landing.country}
											</Typography>
										</Box>
										<EastIcon
											className="destino-arrow"
											sx={{
												fontSize: 18,
												color: "#94A3B8",
												mt: 0.25,
												flexShrink: 0,
												display: { xs: "none", sm: "block" },
												transition: "transform 0.2s ease, color 0.2s ease",
											}}
										/>
									</Box>
								</Box>
							</Grid>
						))}
					</Grid>

					{destinos.length === 0 && (
						<Box
							sx={{
								py: 10,
								textAlign: "center",
								borderRadius: "20px",
								border: "1px dashed rgba(15, 23, 42, 0.12)",
								backgroundColor: "rgba(255,255,255,0.6)",
							}}
						>
							<PublicIcon sx={{ fontSize: 28, color: "#CBD5E1", mb: 1 }} />
							<Typography sx={{ fontWeight: 600, color: "#475569" }}>
								No hay destinos en esta región todavía.
							</Typography>
						</Box>
					)}
				</Box>
			</DashboardLayout>

			<BasicModal
				width={500}
				showModal={showLogin}
				handleClose={() => setShowLogin(false)}
			>
				<Auth setShowLogin={setShowLogin} initialMode={authMode} />
			</BasicModal>
		</>
	);
};

export default DestinosPage;
